/**
 * Shared Activity / Sync-history formatters (WSRPDM-0073).
 *
 * Moved out of TenantDetailPage without behaviour change. History passes
 * `includeUnknownKeys` so extra `runCounters` keys render; Activity keeps
 * the default (standard keys only). Do not combine counters into a health
 * boolean.
 */
import { Box, Typography } from '@mui/material';
import { useCopy } from '@/hooks/useCopy';
import type { RunCounters } from '@/api/wsr/types';

export type TFn = ReturnType<typeof useCopy>['t'];

export const ACTIVITY_KIND_COPY: Record<string, { key: string; fallback: string }> = {
  GENESIS: { key: 'activity.kind.GENESIS', fallback: 'genesis' },
  BACKFILL: { key: 'activity.kind.BACKFILL', fallback: 'backfill' },
  LEADING_EDGE: { key: 'activity.kind.LEADING_EDGE', fallback: 'leading edge' },
  INCREMENTAL: { key: 'activity.kind.INCREMENTAL', fallback: 'incremental' },
};

export function activityKindValue(runKind: string | null | undefined, t: TFn): string {
  if (runKind == null) return '—';
  const copy = ACTIVITY_KIND_COPY[runKind];
  return copy ? t(copy.key, copy.fallback) : '—';
}

export const ACTIVITY_COUNTERS = [
  'fetched',
  'landed',
  'updated',
  'unchanged',
  'failed',
  'ghlUpdates',
  'ghlInserts',
  'ghlFailures',
] as const satisfies readonly (keyof RunCounters)[];

export const FAILURE_COUNTERS = new Set<keyof RunCounters>(['failed', 'ghlFailures']);

const STANDARD_KEYS = new Set<string>(ACTIVITY_COUNTERS);

export function ActivityCountersLine({
  counters,
  includeUnknownKeys = false,
}: {
  counters: RunCounters | Record<string, number> | null | undefined;
  includeUnknownKeys?: boolean;
}) {
  const { t } = useCopy();
  if (counters == null) return null;

  const record = counters as Record<string, unknown>;
  const entries: { key: string; value: number }[] = ACTIVITY_COUNTERS.flatMap((key) => {
    const value = record[key];
    if (typeof value !== 'number' || (value === 0 && !FAILURE_COUNTERS.has(key))) return [];
    return [{ key, value }];
  });

  if (includeUnknownKeys) {
    for (const key of Object.keys(counters)) {
      if (STANDARD_KEYS.has(key)) continue;
      const value = record[key];
      if (typeof value !== 'number') continue;
      entries.push({ key, value });
    }
  }

  if (entries.length === 0) return null;

  return (
    <Typography component="div" variant="caption" color="text.secondary">
      {entries.map(({ key, value }, index) => (
        <Box
          component="span"
          key={key}
          sx={
            FAILURE_COUNTERS.has(key as keyof RunCounters) && value !== 0
              ? { color: 'error.main', fontWeight: 600 }
              : undefined
          }
        >
          {index > 0 ? ' · ' : null}
          {t(`activity.counters.${key}`, `{{n}} ${key}`, { n: value.toLocaleString() })}
        </Box>
      ))}
    </Typography>
  );
}
